import {
  createBrookChallenge,
  moveBrookChallenge,
  brookChallengeView,
} from './66-brook-challenges.mjs';
import {
  createProgressiveIdle,
  moveProgressiveIdle,
  progressiveIdleView,
} from './62-progressive-idle.mjs';
import {
  GAME_PALETTE,
  cell,
  option,
  adjacent,
  reject,
  random,
  shuffled,
} from './53-game-primitives.mjs';
import { marblePuzzle, brookPuzzle } from './59-idle-puzzles.mjs';

const NAMES = ['Teal', 'Coral', 'Violet', 'Gold', 'Blue', 'Pink'];
const families = [
  'vessels',
  'landscape',
  'mosaic',
  'stamps',
  'pond',
  'balance',
  'weave',
  'cloud',
  'paper',
  'brook',
];
const identities = [
  'ball-sort',
  'sand-layers',
  'mosaic-paint',
  'pattern-stamp',
  'ripple-garden',
  'pebble-balance',
  'thread-loom',
  'cloud-shapes',
  'paper-fold',
  'flow-paths',
];
const isPalette = (move) => move.type === 'option' && move.index < 6;
const CREATION_FINISH = {
  'sand-layers': 10,
  'mosaic-paint': 8,
  'pattern-stamp': 13,
  'ripple-garden': 4,
  'thread-loom': 18,
  'cloud-shapes': 4,
  'paper-fold': 9,
};
function hasCreation(state) {
  const d = state.data;
  if (state.gameId === 'sand-layers') return d.layers.length > 0;
  if (state.gameId === 'ripple-garden') return d.rings.length > 0;
  if (state.gameId === 'thread-loom') return d.edited === true;
  if (state.gameId === 'cloud-shapes') return d.board.some(Boolean);
  return d.board.some((value) => value >= 0);
}

/** Create bounded serializable creative surfaces and authored solvable puzzles.
 * Ambient randomness, browser geometry and animation clocks never enter replay.
 * Marbles, balance and brook have winning states. Creative work finishes only
 * when its author explicitly chooses Finish creation; it has no score. */
export function createIdle(state) {
  if (state.gameId === 'flow-paths') return createBrookChallenge(state);
  if (['ball-sort', 'pebble-balance'].includes(state.gameId)) return createProgressiveIdle(state);
  switch (state.gameId) {
    case 'ball-sort':
      state.data = { tubes: marblePuzzle(state).tubes, selected: -1, capacity: 4 };
      break;
    case 'sand-layers':
      state.data = { layers: [], ink: random(state, 6), amount: 2, capacity: 24 };
      break;
    case 'mosaic-paint': {
      const motif = random(state, 3);
      const outline = Array.from({ length: 36 }, (_, i) => {
        const x = i % 6,
          y = Math.floor(i / 6);
        return motif === 0
          ? Math.abs(x - 2.5) + Math.abs(y - 2.5) <= 3
          : motif === 1
            ? x === 1 || x === 4 || y === 1 || y === 4
            : (x + y) % 2 === 0 || x === 2 || y === 3;
      });
      state.data = { board: Array(36).fill(-1), ink: 0, outline, motif, free: false };
      break;
    }
    case 'pattern-stamp':
      state.data = { board: Array(36).fill(-1), ink: 0, stamp: 0, rotation: 0, symmetry: false };
      break;
    case 'ripple-garden':
      state.data = { rings: [], theme: random(state, 3), serial: 0 };
      break;
    case 'pebble-balance': {
      const half = Array.from({ length: 3 }, () => 1 + random(state, 7));
      state.data = {
        weights: shuffled(state, half.concat(half)),
        board: Array(6).fill(0),
        selected: -1,
      };
      break;
    }
    case 'thread-loom':
      state.data = { rows: [0, 0, 0, 0, 0, 0], columns: [3, 3, 3, 3, 3, 3], ink: 0, edited: false };
      break;
    case 'cloud-shapes':
      state.data = { board: Array(36).fill(0), erase: false, radius: 1, sky: random(state, 3) };
      break;
    case 'paper-fold':
      state.data = {
        board: Array(16).fill(-1),
        ink: 0,
        horizontal: false,
        vertical: false,
        paper: random(state, 3),
      };
      break;
    case 'flow-paths': {
      const puzzle = brookPuzzle(state);
      state.data = { path: [0], blocked: puzzle.blocked, checkpoints: puzzle.checkpoints };
      break;
    }
    default:
      reject('Unknown idle game.');
  }
}

function runLength(tube) {
  let count = 0;
  for (let i = tube.length - 1; i >= 0 && tube[i] === tube.at(-1); i--) count++;
  return count;
}

function pourMarbles(state, index) {
  const data = state.data;
  if (data.selected < 0) {
    if (!data.tubes[index]?.length) reject('Choose a tube with marbles.');
    data.selected = index;
    return;
  }
  if (index === data.selected) {
    data.selected = -1;
    return;
  }
  const source = data.tubes[data.selected],
    destination = data.tubes[index];
  if (destination.length >= 4 || (destination.length && destination.at(-1) !== source.at(-1)))
    reject('Choose an empty tube or matching top colour.');
  const count = Math.min(runLength(source), 4 - destination.length);
  destination.push(...source.splice(-count));
  data.selected = -1;
  state.completed = data.tubes.every(
    (tube) => !tube.length || (tube.length === 4 && tube.every((colour) => colour === tube[0])),
  );
}

function stamp(data, index) {
  const patterns = [
    [[0, 0]],
    [
      [0, 0],
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ],
    [
      [0, 0],
      [1, 0],
      [0, 1],
      [1, 1],
    ],
    [
      [0, 0],
      [1, 0],
      [2, 0],
      [0, 1],
    ],
  ];
  const x = index % 6,
    y = Math.floor(index / 6);
  for (let [dx, dy] of patterns[data.stamp]) {
    for (let turn = 0; turn < data.rotation; turn++) [dx, dy] = [-dy, dx];
    const px = x + dx,
      py = y + dy;
    if (px < 0 || px >= 6 || py < 0 || py >= 6) continue;
    data.board[py * 6 + px] = data.ink;
    if (data.symmetry) data.board[py * 6 + 5 - px] = data.ink;
  }
}

function paintCloud(data, index) {
  const x = index % 6,
    y = Math.floor(index / 6);
  for (let i = 0; i < 36; i++) {
    const distance = Math.abs((i % 6) - x) + Math.abs(Math.floor(i / 6) - y);
    if (distance <= data.radius) data.board[i] = data.erase ? 0 : 1;
  }
}

/** Dispatch validated discrete intents into the engine's cloned state.
 * Creative configuration is explicit; brush and pour results remain independent
 * of their renderer's transition duration and never capture browser scrolling. */
export function moveIdle(state, move) {
  if (state.gameId === 'flow-paths') return moveBrookChallenge(state, move);
  if (['ball-sort', 'pebble-balance'].includes(state.gameId))
    return moveProgressiveIdle(state, move);
  const data = state.data,
    index = move.index;
  if (move.type === 'option' && index === CREATION_FINISH[state.gameId]) {
    if (!hasCreation(state)) reject('Create something before finishing.');
    state.completed = true;
    data.completion = 'creation';
    data.events = [{ type: 'complete', reason: 'creation', points: 0 }];
    return;
  }
  switch (state.gameId) {
    case 'ball-sort':
      pourMarbles(state, index);
      break;
    case 'sand-layers':
      if (isPalette(move)) data.ink = index;
      else if (move.type === 'option' && index < 9) data.amount = [1, 2, 4][index - 6];
      else if (move.type === 'option') data.layers = [];
      else {
        const amount = Math.min(data.amount, data.capacity - data.layers.length);
        if (!amount) reject('The landscape is full. Undo or clear to continue.');
        data.layers.push(...Array(amount).fill(data.ink));
      }
      break;
    case 'mosaic-paint':
      if (isPalette(move)) data.ink = index;
      else if (move.type === 'option' && index === 6) data.free = !data.free;
      else if (move.type === 'option') data.board.fill(-1);
      else data.board[index] = data.ink;
      break;
    case 'pattern-stamp':
      if (isPalette(move)) data.ink = index;
      else if (move.type === 'option' && index < 10) data.stamp = index - 6;
      else if (move.type === 'option' && index === 10) data.rotation = (data.rotation + 1) % 4;
      else if (move.type === 'option' && index === 11) data.symmetry = !data.symmetry;
      else if (move.type === 'option') data.board.fill(-1);
      else stamp(data, index);
      break;
    case 'ripple-garden':
      if (move.type === 'option' && index < 3) data.theme = index;
      else if (move.type === 'option') data.rings = [];
      else {
        data.serial++;
        data.rings.push({ x: index % 5, y: Math.floor(index / 5), serial: data.serial });
        if (data.rings.length > 8) data.rings.shift();
      }
      break;
    case 'pebble-balance':
      if (move.type === 'cell') data.selected = index;
      else {
        if (data.selected < 0) reject('Choose a pebble first.');
        data.board[data.selected] = index === 2 ? 0 : index + 1;
        data.selected = -1;
        const sums = [0, 0, 0];
        data.weights.forEach((weight, i) => {
          sums[data.board[i]] += weight;
        });
        state.completed =
          sums[1] > 0 && sums[1] === sums[2] && data.board.every((side) => side !== 0);
      }
      break;
    case 'thread-loom':
      if (isPalette(move)) data.ink = index;
      else {
        const threads = index < 12 ? data.rows : data.columns;
        const position = (index - 6) % 6;
        if (threads[position] !== data.ink) data.edited = true;
        threads[position] = data.ink;
      }
      break;
    case 'cloud-shapes':
      if (move.type === 'cell') paintCloud(data, index);
      else if (index === 0) data.erase = !data.erase;
      else if (index === 1) data.radius = (data.radius + 1) % 3;
      else if (index === 2) data.sky = (data.sky + 1) % 3;
      else data.board.fill(0);
      break;
    case 'paper-fold':
      if (isPalette(move)) data.ink = index;
      else if (move.type === 'option' && index === 6) data.horizontal = !data.horizontal;
      else if (move.type === 'option' && index === 7) data.vertical = !data.vertical;
      else if (move.type === 'option') data.board.fill(-1);
      else data.board[index] = data.ink;
      break;
    case 'flow-paths':
      if (move.type === 'option') {
        if (index === 1) data.path = [0];
        else if (data.path.length > 1) data.path.pop();
      } else {
        if (index === data.path.at(-2)) {
          data.path.pop();
          break;
        }
        if (
          data.path.includes(index) ||
          data.blocked.includes(index) ||
          !adjacent(data.path.at(-1), index, 5)
        )
          reject('Choose connected open ground or step back.');
        const missing = data.checkpoints.filter((checkpoint) => !data.path.includes(checkpoint));
        if (
          (data.checkpoints.includes(index) && missing[0] !== index) ||
          (index === 24 && missing.length)
        )
          reject('Visit the flowers in order before reaching the pool.');
        data.path.push(index);
        state.completed = index === 24;
      }
      break;
  }
}

function palette(ink) {
  return NAMES.map((name, index) => ({
    ...option(index, name),
    colour: index,
    selected: ink === index,
    tool: 'palette',
  }));
}
function tool(index, label, selected = false) {
  return { ...option(index, label), selected };
}
function painted(board, columns, extra = () => ({})) {
  return board.map((value, index) =>
    cell(
      index,
      `${value < 0 ? 'Unpainted' : NAMES[value]} · row ${Math.floor(index / columns) + 1}, column ${(index % columns) + 1}`,
      {
        value,
        colour: value < 0 ? undefined : GAME_PALETTE[value],
        x: index % columns,
        y: Math.floor(index / columns),
        ...extra(index),
      },
    ),
  );
}
function progress(current, total, label) {
  return { current, total, label };
}

/** Derive bounded family-specific artwork and available action metadata.
 * Scene describes simulation results, not DOM, GPU resources or animation time.
 * Discrete intents remain usable beside SVG/canvas artwork for keyboard users. */
export function idleView(state) {
  if (state.gameId === 'flow-paths') return brookChallengeView(state);
  if (['ball-sort', 'pebble-balance'].includes(state.gameId)) return progressiveIdleView(state);
  const data = state.data;
  const view = {
    columns: 6,
    cells: [],
    options: [],
    status: '',
    goal: '',
    family: families[identities.indexOf(state.gameId)],
    scene: {},
    progress: progress(0, 0, 'Creative play'),
  };
  switch (state.gameId) {
    case 'ball-sort': {
      view.columns = 5;
      view.cells = data.tubes.map((tube, index) => {
        const valid =
          data.selected < 0
            ? !!tube.length
            : index === data.selected ||
              (tube.length < 4 &&
                (!tube.length || tube.at(-1) === data.tubes[data.selected].at(-1)));
        return cell(
          index,
          `Tube ${index + 1}: ${tube.map((colour) => NAMES[colour]).join(', ') || 'empty'}; ${4 - tube.length} spaces`,
          {
            layers: [...tube],
            capacity: 4,
            selected: data.selected === index,
            destination: data.selected >= 0 && valid && index !== data.selected,
            disabled: !valid,
            colour: GAME_PALETTE[tube.at(-1)],
          },
        );
      });
      const sorted = data.tubes.filter(
        (tube) => tube.length === 4 && tube.every((colour) => colour === tube[0]),
      ).length;
      view.progress = progress(sorted, 3, `${sorted} of 3 colours sorted`);
      view.status =
        data.selected < 0 ? 'Choose marbles to lift.' : 'Glowing tubes accept these marbles.';
      view.goal = 'Give each colour its own full tube.';
      view.scene = { capacity: 4, selected: data.selected };
      break;
    }
    case 'sand-layers':
      view.columns = 1;
      view.cells = [
        cell(0, `Pour ${data.amount} layers of ${NAMES[data.ink]}`, {
          layers: [...data.layers],
          capacity: data.capacity,
          disabled: data.layers.length >= data.capacity,
        }),
      ];
      view.options = [
        ...palette(data.ink),
        tool(6, 'Fine pour', data.amount === 1),
        tool(7, 'Medium pour', data.amount === 2),
        tool(8, 'Deep pour', data.amount === 4),
        tool(9, 'Clear glass'),
      ];
      view.progress = progress(data.layers.length, 24, `${data.layers.length} of 24 layers`);
      view.goal = 'Choose a colour, then pour into your glass landscape.';
      view.status =
        data.layers.length === 24
          ? 'Your landscape is full. Undo a pour or clear to create again.'
          : 'Each pour adds a new band of colour.';
      view.scene = { layers: [...data.layers], ink: data.ink, amount: data.amount, capacity: 24 };
      break;
    case 'mosaic-paint': {
      view.cells = painted(data.board, 6, (index) => ({
        target: !data.free && data.outline[index],
      }));
      view.options = [
        ...palette(data.ink),
        tool(6, data.free ? 'Show motif' : 'Free canvas', data.free),
        tool(7, 'Clear tiles'),
      ];
      const targets = data.outline.map((target) => data.free || target);
      const total = targets.filter(Boolean).length,
        current = targets.filter((target, index) => target && data.board[index] >= 0).length;
      view.progress = progress(current, total, `${current} of ${total} motif tiles coloured`);
      view.goal = data.free
        ? 'Make a colour composition of your own.'
        : 'Fill the outlined motif with any colours you love.';
      view.status =
        current === total
          ? 'Your motif is filled. Keep painting or share your colours.'
          : 'Choose an ink, then colour a tile.';
      view.scene = { motif: data.motif, outline: targets, free: data.free };
      break;
    }
    case 'pattern-stamp':
      view.cells = painted(data.board, 6);
      view.options = [
        ...palette(data.ink),
        ...['Dot', 'Petal', 'Square', 'Sprig'].map((label, index) =>
          tool(index + 6, label, data.stamp === index),
        ),
        tool(10, `Rotate ${data.rotation * 90}°`),
        tool(11, 'Mirror print', data.symmetry),
        tool(12, 'Clear paper'),
      ];
      view.goal = 'Choose a stamp and ink, then make your print.';
      view.status = data.symmetry
        ? 'Each impression is mirrored across the paper.'
        : 'Your ink stays selected for the next impression.';
      view.scene = {
        stamp: data.stamp,
        rotation: data.rotation,
        symmetry: data.symmetry,
        ink: data.ink,
      };
      break;
    case 'ripple-garden':
      view.columns = 5;
      view.cells = Array.from({ length: 25 }, (_, index) =>
        cell(index, `Ripple at row ${Math.floor(index / 5) + 1}, column ${(index % 5) + 1}`, {
          x: index % 5,
          y: Math.floor(index / 5),
          value: 0,
        }),
      );
      view.options = [
        ...['Morning pond', 'Moon pond', 'Rose pond'].map((label, index) =>
          tool(index, label, data.theme === index),
        ),
        tool(3, 'Still water'),
      ];
      view.goal = 'Touch the pond and watch rings meet.';
      view.status = 'No score. Each touch makes a new ripple.';
      view.scene = {
        theme: data.theme,
        rings: data.rings.map((ring) => ({ ...ring })),
        serial: data.serial,
      };
      break;
    case 'pebble-balance': {
      view.columns = 3;
      const sums = [0, 0, 0];
      view.cells = data.weights.map((weight, index) => {
        sums[data.board[index]] += weight;
        return cell(
          index,
          `${weight} units · ${['unplaced', 'left tray', 'right tray'][data.board[index]]}`,
          { value: weight, weight, side: data.board[index], selected: data.selected === index },
        );
      });
      view.options = ['Left tray', 'Right tray', 'Unplace'].map((label, index) => ({
        ...tool(index, label),
        disabled: data.selected < 0,
      }));
      view.status = `Left ${sums[1]} · Right ${sums[2]}${data.selected < 0 ? ' · choose a pebble' : ' · choose its tray'}`;
      view.goal = 'Place every pebble. Make both trays equal.';
      view.progress = progress(data.board.filter((side) => side !== 0).length, 6, 'Pebbles placed');
      view.scene = {
        left: sums[1],
        right: sums[2],
        imbalance: sums[1] - sums[2],
        selected: data.selected,
      };
      break;
    }
    case 'thread-loom':
      view.cells = Array.from({ length: 36 }, (_, index) => {
        const x = index % 6,
          y = Math.floor(index / 6),
          over = (x + y) % 2 === 0;
        return {
          label: `Warp ${x + 1} ${NAMES[data.columns[x]]}, weft ${y + 1} ${NAMES[data.rows[y]]}`,
          colour: over ? data.columns[x] : data.rows[y],
          warp: data.columns[x],
          weft: data.rows[y],
          over,
          x,
          y,
          disabled: true,
        };
      });
      view.options = [
        ...palette(data.ink),
        ...Array.from({ length: 12 }, (_, index) =>
          tool(index + 6, `${index < 6 ? 'Weft' : 'Warp'} ${(index % 6) + 1}`),
        ),
      ];
      view.goal = 'Choose an ink, then colour a warp or weft thread.';
      view.status = 'Threads cross over and under. Your cloth is the result.';
      view.scene = { rows: [...data.rows], columns: [...data.columns], ink: data.ink };
      break;
    case 'cloud-shapes':
      view.columns = 6;
      view.cells = data.board.map((value, index) =>
        cell(
          index,
          `${data.erase ? 'Erase' : 'Paint'} cloud at row ${Math.floor(index / 6) + 1}, column ${(index % 6) + 1}`,
          {
            value,
            x: index % 6,
            y: Math.floor(index / 6),
            cloud: !!value,
            colour: value ? GAME_PALETTE[4] : undefined,
          },
        ),
      );
      view.options = [
        tool(0, data.erase ? 'Use cloud brush' : 'Use eraser', data.erase),
        tool(1, `Brush ${['fine', 'soft', 'wide'][data.radius]}`),
        tool(2, 'Change sky'),
        tool(3, 'Clear sky'),
      ];
      view.goal = 'Paint a cloud with a soft brush. Erase to shape its edges.';
      view.status = data.erase ? 'Eraser selected.' : 'Cloud brush selected.';
      view.scene = {
        board: [...data.board],
        radius: data.radius,
        erase: data.erase,
        sky: data.sky,
      };
      break;
    case 'paper-fold': {
      view.columns = 4;
      view.cells = painted(data.board, 4);
      view.options = [
        ...palette(data.ink),
        tool(6, 'Horizontal reflection', data.horizontal),
        tool(7, 'Vertical reflection', data.vertical),
        tool(8, 'Clear ink'),
      ];
      const print = Array(64).fill(-1);
      data.board.forEach((value, index) => {
        const x = index % 4,
          y = Math.floor(index / 4);
        for (const px of data.vertical ? [x, 7 - x] : [x])
          for (const py of data.horizontal ? [y, 7 - y] : [y]) print[py * 8 + px] = value;
      });
      view.goal = 'Ink one quarter, then reflect to unfold your print.';
      view.status = `${data.horizontal ? 'Horizontal' : ''}${data.horizontal && data.vertical ? ' and ' : ''}${data.vertical ? 'vertical' : ''}${data.horizontal || data.vertical ? ' reflection' : 'Choose a reflection to reveal symmetry'}.`;
      view.scene = {
        print,
        horizontal: data.horizontal,
        vertical: data.vertical,
        paper: data.paper,
        ink: data.ink,
      };
      break;
    }
    case 'flow-paths': {
      view.columns = 5;
      const missing = data.checkpoints.filter((checkpoint) => !data.path.includes(checkpoint));
      view.cells = Array.from({ length: 25 }, (_, index) => {
        const backtrack = index === data.path.at(-2);
        const next =
          adjacent(data.path.at(-1), index, 5) &&
          !data.path.includes(index) &&
          !data.blocked.includes(index) &&
          (!data.checkpoints.includes(index) || missing[0] === index) &&
          (index !== 24 || !missing.length);
        return cell(
          index,
          index === 0
            ? 'Spring'
            : index === 24
              ? 'Pool'
              : data.blocked.includes(index)
                ? 'Rock'
                : data.checkpoints.includes(index)
                  ? `Flower ${data.checkpoints.indexOf(index) + 1}`
                  : backtrack
                    ? 'Step back'
                    : 'Open ground',
          {
            x: index % 5,
            y: Math.floor(index / 5),
            selected: data.path.includes(index),
            path: data.path.indexOf(index),
            blocked: data.blocked.includes(index),
            checkpoint: data.checkpoints.indexOf(index) + 1,
            destination: next,
            disabled: !next && !backtrack,
            colour: data.path.includes(index) ? GAME_PALETTE[4] : undefined,
          },
        );
      });
      view.options = [tool(0, 'Step back'), tool(1, 'Clear brook')];
      view.goal = 'Visit both flowers in order, then carry water to the pool.';
      view.status = missing.length
        ? `Next: flower ${data.checkpoints.indexOf(missing[0]) + 1}.`
        : 'Both flowers reached. Find the pool.';
      view.progress = progress(2 - missing.length, 2, 'Flowers watered');
      view.scene = {
        path: [...data.path],
        blocked: [...data.blocked],
        checkpoints: [...data.checkpoints],
      };
      break;
    }
  }
  const finish = CREATION_FINISH[state.gameId];
  if (finish !== undefined)
    view.options.push({
      ...option(finish, 'Finish creation'),
      completionAction: true,
      disabled: !hasCreation(state),
    });
  return view;
}
